import React from 'react';
import { Palette, Type, Sparkles, Box, Sun, Moon, AlertTriangle, CheckCircle2, Wand2, Sliders } from 'lucide-react';
import { PageSection, ComponentStyle } from '../../../types';
import { ColorPickerField } from './common/ColorPickerField';
import {
  validateContrast,
  getOptimalTextColor,
  getOptimalMutedTextColor,
  isDarkBackground,
} from '../../../utils/contrast';

interface DesignTabProps {
  section: PageSection;
  onUpdateStyle: (updates: Partial<ComponentStyle>) => void;
}

export const DesignTab: React.FC<DesignTabProps> = ({ section, onUpdateStyle }) => {
  const style = section.style || {};

  const currentBg = style.backgroundColor || '#FFFFFF';
  const currentText = style.textColor || getOptimalTextColor(currentBg);
  const colorMode = style.colorMode || 'auto';

  // Contrast analysis
  const contrast = validateContrast(currentBg, currentText);

  const handleBgColorChange = (newBg: string) => {
    if (colorMode === 'auto') {
      const optimalText = getOptimalTextColor(newBg);
      const isDark = isDarkBackground(newBg);
      onUpdateStyle({
        backgroundColor: newBg,
        textColor: optimalText,
        headingColor: isDark ? '#FFFFFF' : '#0F172A',
        mutedTextColor: getOptimalMutedTextColor(newBg),
      });
    } else {
      onUpdateStyle({ backgroundColor: newBg });
    }
  };

  const handleAutoAdjust = () => {
    const optimalText = getOptimalTextColor(currentBg);
    const isDark = isDarkBackground(currentBg);
    onUpdateStyle({
      textColor: optimalText,
      headingColor: isDark ? '#FFFFFF' : '#0F172A',
      mutedTextColor: getOptimalMutedTextColor(currentBg),
      colorMode: 'auto',
    });
  };

  const themeStylePresets = [
    {
      name: 'Temiz Açık',
      bg: '#F8FAFC',
      text: '#0F172A',
      heading: '#0F172A',
      accent: '#2563EB',
      icon: Sun,
    },
    {
      name: 'Koyu Premium',
      bg: '#0B1120',
      text: '#F8FAFC',
      heading: '#FFFFFF',
      accent: '#38BDF8',
      icon: Moon,
    },
    {
      name: 'Minimalist Beyaz',
      bg: '#FFFFFF',
      text: '#18181B',
      heading: '#09090B',
      accent: '#18181B',
      icon: Sun,
    },
    {
      name: 'Lacivert / Deniz',
      bg: '#0F172A',
      text: '#F1F5F9',
      heading: '#FFFFFF',
      accent: '#06B6D4',
      icon: Moon,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Quick Theme Presets */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">Hazır Renk Temaları</h4>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {themeStylePresets.map((preset, idx) => {
            const Icon = preset.icon;
            return (
              <button
                key={idx}
                type="button"
                onClick={() =>
                  onUpdateStyle({
                    backgroundColor: preset.bg,
                    textColor: preset.text,
                    headingColor: preset.heading,
                    colorMode: 'auto',
                  })
                }
                className="p-2.5 rounded-xl border border-slate-700 hover:border-blue-500 bg-slate-900/70 text-left transition group"
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] font-bold text-slate-200 group-hover:text-blue-400 transition">
                    {preset.name}
                  </span>
                  <Icon className="w-3 h-3 text-slate-400" />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-4 h-4 rounded-full border border-slate-700" style={{ backgroundColor: preset.bg }} />
                  <span className="w-4 h-4 rounded-full border border-slate-700" style={{ backgroundColor: preset.text }} />
                  <span className="w-4 h-4 rounded-full border border-slate-700" style={{ backgroundColor: preset.accent }} />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Colors & Contrast Engine */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Palette className="w-3.5 h-3.5 text-blue-400" />
            <h4 className="text-xs font-bold text-slate-200">Bölüm Renk Mimarisi</h4>
          </div>

          {/* Auto vs Manual Mode Switch */}
          <div className="flex bg-slate-900 rounded-lg p-0.5 border border-slate-700">
            <button
              type="button"
              onClick={() => onUpdateStyle({ colorMode: 'auto' })}
              className={`px-2 py-1 rounded-md text-[10px] font-bold transition flex items-center gap-1 ${
                colorMode === 'auto'
                  ? 'bg-blue-600 text-white shadow-xs'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <Wand2 className="w-2.5 h-2.5" />
              <span>Otomatik</span>
            </button>
            <button
              type="button"
              onClick={() => onUpdateStyle({ colorMode: 'manual' })}
              className={`px-2 py-1 rounded-md text-[10px] font-bold transition flex items-center gap-1 ${
                colorMode === 'manual'
                  ? 'bg-blue-600 text-white shadow-xs'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <Sliders className="w-2.5 h-2.5" />
              <span>Özel</span>
            </button>
          </div>
        </div>

        {/* Live Contrast Status Feedback Banner */}
        <div
          className={`p-3 rounded-xl border flex items-center justify-between text-xs gap-3 ${
            contrast.status === 'good'
              ? 'bg-emerald-950/30 border-emerald-500/30 text-emerald-300'
              : contrast.status === 'warning'
              ? 'bg-amber-950/30 border-amber-500/30 text-amber-300'
              : 'bg-rose-950/30 border-rose-500/40 text-rose-300'
          }`}
        >
          <div className="flex items-center gap-2">
            {contrast.status === 'good' ? (
              <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400" />
            ) : (
              <AlertTriangle className="w-4 h-4 shrink-0 text-amber-400" />
            )}
            <div className="space-y-0.5">
              <div className="font-bold flex items-center gap-1.5">
                <span>Kontrast: {contrast.ratioFormatted}</span>
                <span className="text-[10px] px-1.5 py-0.2 rounded bg-black/40 font-mono">
                  {contrast.isCompliant ? 'WCAG AA Uyumlu' : 'Düşük Kontrast'}
                </span>
              </div>
              <p className="text-[11px] opacity-80">
                {contrast.isCompliant
                  ? 'Metin ve arka plan renkleri net ve okunabilir.'
                  : 'Yazı rengi arka plan ile karışabilir.'}
              </p>
            </div>
          </div>

          {!contrast.isCompliant && (
            <button
              type="button"
              onClick={handleAutoAdjust}
              className="px-2.5 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-950 font-extrabold text-[10px] shrink-0 transition shadow-sm active:scale-95"
            >
              Uyumla
            </button>
          )}
        </div>

        {/* Background Color Picker */}
        <ColorPickerField
          label="Bölüm Arka Plan Rengi"
          value={style.backgroundColor}
          defaultValue="#FFFFFF"
          onChange={handleBgColorChange}
        />

        {/* Text Color Picker */}
        <ColorPickerField
          label="Metin / Yazı Rengi"
          value={style.textColor}
          defaultValue={getOptimalTextColor(currentBg)}
          onChange={(color) =>
            onUpdateStyle({
              textColor: color,
              colorMode: 'manual',
            })
          }
          helper={
            colorMode === 'auto'
              ? 'Otomatik modda arka plana göre en yüksek kontrastlı renk seçilir.'
              : 'Manuel olarak seçtiğiniz renk korunur.'
          }
        />

        {/* Detailed semantic colors available in manual mode */}
        {colorMode === 'manual' && (
          <div className="space-y-3.5 pt-2 border-t border-slate-700/60">
            <h5 className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
              Detaylı Renk Kontrolleri
            </h5>

            <ColorPickerField
              label="Başlık Rengi"
              value={style.headingColor}
              defaultValue={isDarkBackground(currentBg) ? '#FFFFFF' : '#0F172A'}
              onChange={(color) => onUpdateStyle({ headingColor: color })}
            />

            <ColorPickerField
              label="İkincil / Açıklama Rengi"
              value={style.mutedTextColor}
              defaultValue={getOptimalMutedTextColor(currentBg)}
              onChange={(color) => onUpdateStyle({ mutedTextColor: color })}
            />

            <ColorPickerField
              label="Buton Arka Plan Rengi"
              value={style.buttonBgColor}
              defaultValue="#2563EB"
              onChange={(color) => onUpdateStyle({ buttonBgColor: color })}
            />

            <ColorPickerField
              label="Buton Yazı Rengi"
              value={style.buttonTextColor}
              defaultValue={getOptimalTextColor(style.buttonBgColor || '#2563EB')}
              onChange={(color) => onUpdateStyle({ buttonTextColor: color })}
            />

            <ColorPickerField
              label="Kart Arka Plan Rengi"
              value={style.cardBgColor}
              defaultValue={isDarkBackground(currentBg) ? '#0F172A' : '#FFFFFF'}
              onChange={(color) => onUpdateStyle({ cardBgColor: color })}
            />
          </div>
        )}
      </div>

      {/* Typography Scale */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center gap-2">
          <Type className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">Tipografi Boyutları</h4>
        </div>

        {/* Heading Size */}
        <div className="space-y-1.5">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Başlık Boyutu</label>
          <div className="grid grid-cols-4 gap-1.5">
            {[
              { id: '2xl', label: 'Küçük (2XL)' },
              { id: '3xl', label: 'Standart (3XL)' },
              { id: '4xl', label: 'Büyük (4XL)' },
              { id: '5xl', label: 'Dev (5XL)' },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => onUpdateStyle({ headingFontSize: opt.id })}
                className={`py-2 px-1 rounded-xl text-[10px] font-bold transition text-center ${
                  (style.headingFontSize || '3xl') === opt.id
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-slate-900 text-slate-300 border border-slate-700 hover:bg-slate-800'
                }`}
              >
                {opt.id.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Body Font Size */}
        <div className="space-y-1.5">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Gövde / Paragraf Boyutu</label>
          <div className="grid grid-cols-3 gap-1.5">
            {[
              { id: 'sm', label: 'Kompakt (14px)' },
              { id: 'base', label: 'Standart (16px)' },
              { id: 'lg', label: 'Geniş (18px)' },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => onUpdateStyle({ fontSize: opt.id })}
                className={`py-2 px-1 rounded-xl text-[10px] font-bold transition text-center ${
                  (style.fontSize || 'base') === opt.id
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-slate-900 text-slate-300 border border-slate-700 hover:bg-slate-800'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Card Corners & Border Radius */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center gap-2">
          <Box className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">Kart & Köşe Yuvarlaklığı</h4>
        </div>

        <div className="grid grid-cols-4 gap-1.5">
          {[
            { id: 'none', label: 'Keskin (0px)' },
            { id: 'md', label: 'Hafif (8px)' },
            { id: 'xl', label: 'Modern (16px)' },
            { id: '3xl', label: 'Yuvarlak (24px)' },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => onUpdateStyle({ borderRadius: opt.id })}
              className={`py-2 px-1 rounded-xl text-[10px] font-bold transition text-center ${
                (style.borderRadius || 'xl') === opt.id
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-slate-900 text-slate-300 border border-slate-700 hover:bg-slate-800'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
